import { useEffect, useState } from "react";
import { CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangle, ClipboardList, DollarSign, Factory, Leaf, PackageCheck, ShieldAlert, Sparkles, TrendingUp } from "lucide-react";
import { api, IDR, NUM, DATE, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const CHART_COLORS = ["#059669", "#0891B2", "#0B4F78", "#F59E0B"];

function Kpi({ icon: Icon, label, value, hint, tone = "emerald" }) {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    navy: "bg-slate-100 text-slate-700 border-slate-200",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    cyan: "bg-cyan-50 text-cyan-700 border-cyan-100",
  };
  return (
    <div className="gc-kpi" data-testid={`kpi-${label}`}>
      <div className="flex items-center justify-between">
        <span className="gc-label">{label}</span>
        <span className={`inline-flex h-9 w-9 rounded-xl border ${toneMap[tone]} items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </span>
      </div>
      <div className="font-cabinet text-3xl font-black text-slate-900 mt-2">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user, orgs } = useAuth();
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: a }] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/dashboard/recent-activity?limit=8"),
      ]);
      setSummary(s); setActivity(a);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const currentOrg = orgs.find((o) => o.id === user?.active_org_id);
  const isDemo = currentOrg?.is_demo;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="gc-label">Ringkasan</span>
          <h1 className="gc-h1 mt-1">Selamat datang, {user?.name?.split(" ")[0]}</h1>
          <p className="mt-2 text-slate-500">{currentOrg?.name || "Belum ada organisasi aktif"} • Update: {new Date().toLocaleString("id-ID")}</p>
        </div>
        {isDemo && (
          <div className="flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 text-sm font-semibold" data-testid="banner-demo">
            <Sparkles className="w-4 h-4" /> DATA DEMO — bukan data produksi
          </div>
        )}
      </div>

      {loading || !summary ? (
        <div className="gc-card text-sm text-slate-500">Memuat…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Kpi icon={Factory} label="Produk Aktif" value={NUM(summary.products, 0)} tone="emerald" />
            <Kpi icon={ClipboardList} label="Batch Bulan Ini" value={NUM(summary.batches_month, 0)} hint={`Total batch: ${NUM(summary.batches_total, 0)}`} tone="cyan" />
            <Kpi icon={DollarSign} label="Total Biaya Produksi" value={IDR(summary.total_production_cost)} hint="Akumulasi HPP aktual" tone="navy" />
            <Kpi icon={TrendingUp} label="HPP Rata-rata" value={IDR(summary.avg_hpp)} hint="Per unit produk baik" tone="emerald" />
            <Kpi icon={PackageCheck} label="Produk Baik" value={NUM(summary.good_qty, 0)} tone="emerald" />
            <Kpi icon={AlertTriangle} label="Produk Gagal / Waste" value={NUM(summary.reject_qty, 0)} tone="amber" />
            <Kpi icon={Leaf} label="Biaya Lingkungan" value={IDR(summary.env_cost)} tone="cyan" />
            <Kpi icon={ShieldAlert} label="HPP Menunggu Persetujuan" value={NUM(summary.pending_hpp, 0)} tone="rose" />
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            <div className="gc-card lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="gc-label">Tren HPP</span>
                  <h3 className="gc-h3 mt-1">HPP rata-rata per bulan</h3>
                </div>
              </div>
              <div className="h-72">
                {summary.trend?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={summary.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `Rp ${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => IDR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                      <Line type="monotone" dataKey="avg_hpp" stroke="#059669" strokeWidth={3} dot={{ r: 5, fill: "#059669" }} activeDot={{ r: 7 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">Belum ada perhitungan HPP aktual untuk ditampilkan.</div>
                )}
              </div>
            </div>

            <div className="gc-card">
              <span className="gc-label">Komposisi Biaya</span>
              <h3 className="gc-h3 mt-1">Sebaran Biaya HPP</h3>
              <div className="h-64 mt-3">
                {(summary.cost_composition || []).reduce((a, x) => a + x.value, 0) > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={summary.cost_composition} dataKey="value" nameKey="label" innerRadius={50} outerRadius={90} paddingAngle={2}>
                        {summary.cost_composition.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => IDR(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">Belum ada data biaya.</div>
                )}
              </div>
            </div>
          </div>

          <div className="gc-card">
            <span className="gc-label">Aktivitas Terbaru</span>
            <h3 className="gc-h3 mt-1">Audit Log Ringkas</h3>
            <div className="mt-4 divide-y divide-slate-100">
              {activity.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">Belum ada aktivitas.</div>}
              {activity.map((a) => (
                <div key={a.id} className="py-3 flex items-center gap-3 text-sm">
                  <div className="h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-800 truncate"><span className="font-semibold">{a.actor_email || "sistem"}</span> · <span className="text-emerald-700 font-mono text-xs">{a.action}</span> {a.entity && <span className="text-slate-500">— {a.entity}</span>}</div>
                    <div className="text-[11px] text-slate-400">{DATE(a.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
